import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { ScheduleSessions } from "./dialog";
import { SchedulerInputProvider, useSchedulerInput } from "./input-provider";

export interface OrderSetSchedulerDialogProps {
  /** A single patient's order sets, in the desired scheduling sequence. */
  orderSetIds: readonly string[];
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

/** Global-overlay adapter. Callers supply identities, never presentation or workflow state. */
export function OrderSetSchedulerDialog({ orderSetIds, ...controls }: OrderSetSchedulerDialogProps) {
  return (
    <SchedulerInputProvider orderSetIds={orderSetIds}>
      <OrderSetSchedulerContent {...controls} />
    </SchedulerInputProvider>
  );
}

function OrderSetSchedulerContent({ open, onClose, onExited }: Omit<OrderSetSchedulerDialogProps, "orderSetIds">) {
  const input = useSchedulerInput();
  if (input.status !== "ready") {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        onOpenChangeComplete={(next) => {
          if (!next) onExited();
        }}
      >
        <DialogContent aria-describedby={undefined} className="sm:max-w-md">
          <DialogTitle>Schedule sessions</DialogTitle>
          {input.status === "loading" ? (
            <output className="text-sm text-muted-foreground">Loading sessions…</output>
          ) : (
            <p role="alert" className="text-sm text-destructive">
              {input.message}
            </p>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <ScheduleSessions.Provider
      {...input.data}
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <ScheduleSessions.Frame onExited={onExited}>
        <ScheduleSessions.Header />
        <ScheduleSessions.Body />
        <ScheduleSessions.Footer />
      </ScheduleSessions.Frame>
    </ScheduleSessions.Provider>
  );
}
